import { cn, memoClassNames } from '@/utils';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/card';
import { TrendingUp } from 'lucide-react';
import { cva, VariantProps } from 'class-variance-authority';

const classes = memoClassNames('StatCard', [
  'root',
  'icon',
  'body',
  'value',
  'change',
]);

const rootVariants = cva(' mm:relative mm:shadow-sm', {
  variants: {
    variant: {
      default: 'mm:text-primary mm:[&_.MemoStatCard-icon]:bg-primary/10',
      primary:
        'mm:bg-primary mm:text-primary-foreground mm:border-none mm:[&_.MemoStatCard-icon]:bg-primary-foreground/10',
      outline: 'mm:bg-transparent',
      secondary:
        'mm:bg-secondary mm:text-secondary-foreground mm:[&_.MemoStatCard-icon]:bg-secondary-foreground/10',
    },
  },
  defaultVariants: {
    variant: 'default',
  },
});

export type StatsCardProps = React.ComponentProps<'div'> &
  VariantProps<typeof rootVariants> & {
    label?: React.ReactNode;
    description?: React.ReactNode;
    value?: React.ReactNode;
    change?: React.ReactNode;
    trending?: 'up' | 'down';
    icon?: React.ElementType;
  };

export const StatsCard = ({
  label,
  description,
  value,
  change,
  icon: Icon = TrendingUp,
  trending,
  variant = 'default',
  className,
  ...others
}: StatsCardProps) => {
  return (
    <Card
      {...others}
      className={cn(classes.root, rootVariants({ variant, className }))}
      data-trending={trending}
    >
      <CardHeader
        className={cn(
          'mm:flex mm:flex-row mm:items-center mm:justify-between mm:space-y-0 mm:p-3 mm:pb-0',
          { 'mm:pb-2': Boolean(description) }
        )}
      >
        <CardTitle className="mm:text-sm mm:font-medium">
          {label}
          {description && (
            <p className="mm:text-xs mm:text-muted-foreground">{description}</p>
          )}
        </CardTitle>
        <div
          className={`${classes.icon} mm:p-2 mm:rounded-lg mm:bg-card-foreground/10`}
        >
          <Icon className="mm:size-5" />
        </div>
      </CardHeader>

      <CardContent className="mm:p-3 mm:pt-0">
        <div className="mm:flex mm:gap-3 mm:items-baseline">
          <span className="mm:text-2xl mm:font-bold">{value}</span>
          <span
            className={`${classes.change} mm:text-xs mm:text-muted-foreground mm:in-data-[trending=up]:text-success mm:in-data-[trending=down]:text-destructive mm:font-medium`}
          >
            {change}
          </span>
        </div>
      </CardContent>
    </Card>
  );
};

StatsCard.displayName = 'StatsCard';
